import { format } from "date-fns";

import { Place } from "../types/flight/Place";
import {
  MultiCityLegStateType,
  FlightOption,
  returnFlightPairs,
  FareOption,
  BaggageInfo,
  FlightLeg,
  BudgetPolicy,
  BudgetPolicyDurationLimit,
  BUDGET_DURATION_BASIS,
  ExtendedPrice,
  VIOLATION_ACTION,
} from "../types/flight";
import { TravelerCount } from "../types/flight/TravelerCount";
import { TimeUtility } from "./timeutility";
import { ROUTE_TYPE } from "../enums/ROUTE_TYPE";
import {
  FORMAT_MMM_D,
  FORMAT_YYY_MM_DD,
} from "@/core/constants/dateTimeFormats";
import { toZonedTime } from "date-fns-tz";
import {
  getCurrencyFormatter,
  parseIntoCurrentDateIgnoringTimezone,
} from "./helpers";

export function calculatePriceRangeFilters(
  flightOptions: FlightOption[],
): Array<number> {
  let fareOptions: Array<number> = [];
  flightOptions.forEach((flightOption) =>
    flightOption.fare_options.forEach((fareOption) =>
      fareOptions.push(getGrossFare(fareOption)),
    ),
  );
  fareOptions = fareOptions.sort((a, b) => a - b);
  return [fareOptions[0], fareOptions[fareOptions.length - 1]];
}

export function getAirlineFilters(
  flightOptions: FlightOption[],
): Array<{ label: string; value: string }> {
  const airlinesCount: any = {};
  flightOptions.forEach((flightOption) => {
    if (airlinesCount[flightOption.airline.code]) {
      airlinesCount[flightOption.airline.code].count++;
    } else {
      airlinesCount[flightOption.airline.code] = {
        name: flightOption.airline.name,
        code: flightOption.airline.code,
        count: 1,
      };
    }
  });
  return Object.values(airlinesCount).map((item: any) => ({
    label: `${item.name} (${item.count})`,
    value: item.code || "",
  }));
}

export function filterFlightsByPrice(
  flightOptions: FlightOption[],
  minPriceRange: number,
  maxPriceRange: number,
) {
  return flightOptions.filter((flightOption) => {
    const fareOptions = flightOption.fare_options.filter((fareOption) => {
      const grossFare = getGrossFare(fareOption);
      return grossFare >= minPriceRange && grossFare <= maxPriceRange;
    });
    return !!fareOptions.length;
  });
}

export function getGrossFare(fareOption: FareOption): number {
  if (fareOption.price.ag_gross_fare && fareOption.price.ag_gross_fare.value) {
    return fareOption.price.ag_gross_fare.value;
  } else {
    return fareOption.price.gross_fare.value;
  }
}

export function getTotalAmountAfterPricing(fareOption: FareOption): number {
  return fareOption.price.total_amount_after_pricing?.value;
}

export function getAirborneMinutes(fareOption: FareOption): number {
  return (fareOption.segments ?? []).reduce(
    (total, segment) => total + (segment.duration_minutes || 0),
    0,
  );
}

export function getBudgetViolationAction(
  policy: BudgetPolicy | null | undefined,
  airborneMinutes: number,
  fareOption: FareOption,
  travelerCount?: TravelerCount | null,
): VIOLATION_ACTION | null {
  if (
    !policy ||
    policy.duration_basis !== BUDGET_DURATION_BASIS.FLYING_TIME ||
    !policy.duration_limits?.length
  ) {
    return null;
  }

  const limit = policy.duration_limits.find((item) =>
    isWithinBudgetDuration(airborneMinutes, item),
  );

  if (!limit || limit.action === VIOLATION_ACTION.HIDE) {
    return null;
  }

  const total = getTotalAmountAfterPricing(fareOption);
  const currency = fareOption.price.total_amount_after_pricing?.currency;

  if (exceedsBudgetCap(total, currency, limit.max_fare)) {
    return limit.action;
  }

  const travelerHeadcount = getTravelerHeadcount(travelerCount);
  const farePerTraveler =
    travelerHeadcount > 0 ? total / travelerHeadcount : null;

  if (
    farePerTraveler != null &&
    exceedsBudgetCap(farePerTraveler, currency, limit.max_fare_per_traveler)
  ) {
    return limit.action;
  }

  return null;
}

function isWithinBudgetDuration(
  airborneMinutes: number,
  limit: BudgetPolicyDurationLimit,
): boolean {
  const aboveMinimum =
    limit.min_minutes == null || airborneMinutes >= limit.min_minutes;
  const belowMaximum =
    limit.max_minutes == null || airborneMinutes <= limit.max_minutes;

  return aboveMinimum && belowMaximum;
}

function exceedsBudgetCap(
  amount: number,
  currency: string | null | undefined,
  cap: ExtendedPrice | null,
): boolean {
  if (!cap || typeof cap.value !== "number" || typeof amount !== "number") {
    return false;
  }

  if (!currency || cap.currency !== currency) {
    return false;
  }

  return amount > cap.value;
}

function getTravelerHeadcount(travelerCount?: TravelerCount | null): number {
  if (!travelerCount) {
    return 0;
  }

  return (
    (travelerCount.adult_count || 0) +
    (travelerCount.child_count || 0) +
    (travelerCount.infant_count || 0)
  );
}

function formatBudgetMinutesLabel(minutes: number): string {
  return TimeUtility.getDurationInTextByMinutes(minutes) || "0 mins";
}

export function formatBudgetDurationBracketLabel(
  limit: BudgetPolicyDurationLimit,
): string {
  const minMinutes = limit.min_minutes ?? 0;

  if (limit.max_minutes == null) {
    return `${formatBudgetMinutesLabel(minMinutes)}+`;
  }

  const maxMinutes = limit.max_minutes;
  const minLabel = formatBudgetMinutesLabel(minMinutes);
  const maxLabel = formatBudgetMinutesLabel(maxMinutes);

  return `${minLabel} – ${maxLabel}`;
}

export function formatBudgetPolicyPrice(
  price: ExtendedPrice | null | undefined,
): string {
  if (!price || typeof price.value !== "number") {
    return "—";
  }

  return getCurrencyFormatter(price.currency).format(price.value);
}

export function filterReturnFlightsByPrice(
  flightOptions: returnFlightPairs[],
  minPriceRange: number,
  maxPriceRange: number,
) {
  return flightOptions.filter(() => {
    const fareOptions = flightOptions.filter((fareOption) => {
      const grossFare = fareOption.gross_fare;
      return grossFare >= minPriceRange && grossFare <= maxPriceRange;
    });
    return !!fareOptions.length;
  });
}
export function filterFlightsByAirlines(
  flightOptions: FlightOption[],
  airlines: Array<string> = [],
) {
  let tempFilteredData: Array<FlightOption> = [];
  tempFilteredData = flightOptions.filter((item) =>
    airlines.includes(item.airline.code),
  );
  return tempFilteredData;
}

export function filterReturnFlightsByAirlines(
  flightOptions: returnFlightPairs[],
  airlines: Array<string> = [],
) {
  let tempFilteredData: Array<returnFlightPairs> = [];
  tempFilteredData = flightOptions.filter((item) =>
    airlines.includes(item.code),
  );
  return tempFilteredData;
}

export function matchesStopsFilter(
  stopsLength: number,
  activeStops: number[],
): boolean {
  if (activeStops.length === 0) return true;
  return stopsLength <= Math.max(...activeStops);
}

export function filterFlightsByStops(
  flightOptions: FlightOption[],
  stops: string[] = [],
): FlightOption[] {
  return flightOptions
    .map((flightOption) => ({
      ...flightOption,
      fare_options: flightOption.fare_options.filter((fareOption) => {
        const numStops = fareOption.segments.length;
        return matchesStopsFilter(numStops, stops.map(Number));
      }),
    }))
    .filter((flightOption) => flightOption.fare_options.length > 0);
}
export function filterReturnFlightsByStops(
  flightOptions: returnFlightPairs[],
  stops: string[] = [],
): returnFlightPairs[] {
  return flightOptions
    .filter((flightOption) => {
      // Filter the flight options based on the number of stops in FO_1.segments
      const numStops = flightOption.FO_1.segments.length;
      return matchesStopsFilter(numStops, stops.map(Number));
    })
    .map((flightOption) => ({
      ...flightOption,
      fare_options: flightOption.FO_1,
    }));
}
export const filterFlightsByDepartureTime = (
  flightOptions: FlightOption[],
  departureTime: string[] = [],
) => {
  const departureHours = departureTime.flatMap((time) => {
    const [start, end] = time.split("-").map(Number);
    return Array.from({ length: end - start + 1 }, (_, i) =>
      (start + i).toString().padStart(2, "0"),
    );
  });

  return flightOptions.filter((flightOption) => {
    const flightDepartureHour = TimeUtility.parsedFlightTimeorDate(
      flightOption.departure_time,
      "HH",
    );

    return departureHours.includes(flightDepartureHour);
  });
};
export const filterReturnFlightsByDepartureTime = (
  flightOptions: returnFlightPairs[],
  departureTime: string[] = [],
) => {
  const departureHours = departureTime.flatMap((time) => {
    const [start, end] = time.split("-").map(Number);
    return Array.from({ length: end - start + 1 }, (_, i) =>
      (start + i).toString().padStart(2, "0"),
    );
  });

  return flightOptions.filter((flightOption) => {
    const flightDepartureTime = flightOption.firstLegDepartureTime;
    const [hours] = flightDepartureTime.split(":");
    return departureHours.includes(hours);
  });
};

export const mapFlightAirport = (airport: Place) => ({
  ...airport,
  label: `${airport.city} ${airport.iata_code}`,
});

export const formatCurrency = (amount = 0, currency = "PKR") => {
  const formatter = getCurrencyFormatter(currency);
  if (!formatter) return "";
  const formattedNumber = formatter.format(Math.trunc(amount));
  const numberWithoutCurrency = formattedNumber.replace(/^[^\d]+/, "").trim();
  return `${currency} ${numberWithoutCurrency}`;
};

export const renderBaggageInfo = (baggage_info: BaggageInfo) => {
  const { pieces, weight, unit } = baggage_info;

  if (pieces > 0) {
    const baggageText = `${pieces} ${pieces > 1 ? "pieces" : "piece"}`;

    return weight > 0
      ? `${baggageText} of ${weight} ${unit}`
      : `${baggageText} (As per airline policy)`;
  } else if (pieces < 1 && weight < 1) {
    return "Baggage included (As per airline policy)";
  } else {
    return `${weight} ${unit}`;
  }
};

export const formatMultiCityQueryParam = (
  data: Array<MultiCityLegStateType>,
) => {
  return data.reduce((acc, curr: MultiCityLegStateType) => {
    const formatedDate = format(curr.departure_date, FORMAT_YYY_MM_DD);
    const originIataCode = curr.origin ? curr.origin.iata_code : "";
    const destinationIataCode = curr.destination
      ? curr.destination.iata_code
      : "";

    const temp = `${originIataCode},${destinationIataCode},${formatedDate},`;
    acc += temp;

    return acc;
  }, "");
};

export const formatMultiCityQueryParamToArray = (trips: string) => {
  return trips.split(",").reduce(
    (acc, curr: string, index: number, array: string[]) => {
      if (index % 3 === 0) {
        acc.push({
          origin: curr,
          destination: array[index + 1],
          departure_date: array[index + 2],
        });
      }
      return acc;
    },
    [] as { origin: string; destination: string; departure_date: string }[],
  );
};

export const formatTripDates = (trips: string, route_type: string) => {
  if (route_type === ROUTE_TYPE.MULTI_CITY) {
    const formattedTrips = formatMultiCityQueryParamToArray(trips as string);
    const startDate = formattedTrips[0].departure_date;
    const endDate = formattedTrips[formattedTrips.length - 1].departure_date;
    return `${format(parseIntoCurrentDateIgnoringTimezone(startDate), FORMAT_MMM_D)} - ${format(
      parseIntoCurrentDateIgnoringTimezone(endDate),
      FORMAT_MMM_D,
    )}`;
  } else {
    // Don't delete origin and destination variables because there string is splited
    const [, , departure_date, return_date] = trips.split(",");
    if (return_date) {
      return `${format(parseIntoCurrentDateIgnoringTimezone(departure_date), FORMAT_MMM_D)} - ${format(
        parseIntoCurrentDateIgnoringTimezone(return_date),
        FORMAT_MMM_D,
      )}`;
    } else {
      return `${format(parseIntoCurrentDateIgnoringTimezone(departure_date), FORMAT_MMM_D)}`;
    }
  }
};

export function extractMarketingAirlineCodes(
  flightLegs: FlightLeg[],
): string[] {
  const codes = flightLegs
    .flatMap((leg) => leg.segment)
    .map((segment) => segment.marketing_airline.code?.toUpperCase())
    .filter(Boolean) as string[];

  return [...new Set(codes)];
}

export const handleConvertUTCtoZonedTime = (date: Date): string => {
  const ALLOWED_TIMEZONES = ["Asia/Dubai", "Asia/Riyadh"];

  const currentTimezone: string =
    Intl.DateTimeFormat().resolvedOptions().timeZone;
  const validTimezone = ALLOWED_TIMEZONES.includes(currentTimezone)
    ? currentTimezone
    : "Asia/Karachi";
  const convertedDate = toZonedTime(date, validTimezone);
  const formattedDate = convertedDate.toISOString();

  return formattedDate;
};

export function splitCamelCase(str: string) {
  // This regex looks for the following patterns:
  // 1. A lowercase letter followed by an uppercase letter
  // 2. A number followed by an uppercase letter
  // 3. An uppercase letter followed by an uppercase letter and then a lowercase letter
  return str
    .replace(/([a-z0-9])([A-Z])|([A-Z])([A-Z][a-z])/g, "$1$3 $2$4")
    .trim();
}
